import { Middleware, UnknownAction } from 'redux'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import {
  resetSourceScope,
  setColumnOrder,
  setColumnSizing,
  setColumnVisibility,
  setSourceScope,
} from '@disco/state-management/reducers/search-page'
import { getUserSettings } from '@disco/state-management/selectors/user'
import { PRELOAD_DATA_ON_LOGIN } from '@disco/constants/ActionTypes'

export const SEARCH_TABLE_VISIBILITY_KEY = 'search_column_visibility'
export const SEARCH_TABLE_ORDER_KEY = 'search_column_order'
export const SEARCH_TABLE_SIZING_KEY = 'search_column_sizing'
// MDS scope choice (STL-1173) — persisted per-user via updateUserSettings
// rather than localStorage, so it survives across devices/sessions and
// doesn't bleed between users on a shared computer.
export const SEARCH_SOURCE_SCOPE_KEY = 'search_source_scope'

const settingsByAction = [
  {
    match: setColumnVisibility.match,
    key: SEARCH_TABLE_VISIBILITY_KEY,
    getValue: (action: ReturnType<typeof setColumnVisibility>) =>
      action.payload,
  },
  {
    match: setColumnOrder.match,
    key: SEARCH_TABLE_ORDER_KEY,
    getValue: (action: ReturnType<typeof setColumnOrder>) => action.payload,
  },
  {
    match: setColumnSizing.match,
    key: SEARCH_TABLE_SIZING_KEY,
    getValue: (action: ReturnType<typeof setColumnSizing>) => action.payload,
  },
  {
    match: setSourceScope.match,
    key: SEARCH_SOURCE_SCOPE_KEY,
    getValue: (action: ReturnType<typeof setSourceScope>) => action.payload,
  },
  {
    match: resetSourceScope.match,
    key: SEARCH_SOURCE_SCOPE_KEY,
    getValue: () => null,
  },
]

/**
 * A middleware that handles search preferences by:
 * 1. Loading saved user preferences after data preload (overrides defaults set by reducer)
 * 2. Saving column visibility/order/sizing/source-scope changes to user settings
 */
const searchPreferencesSync: Middleware<object, any> = store => {
  // On first load, we need to load the settings from the server and set them in the redux store
  // but we don't want to trigger an update at the same time, as it is not necessary
  let isLoadingFromSettings = false

  return next => (action: UnknownAction) => {
    // Process the action first (standard middleware pattern)
    const result = next(action)

    // Load saved user preferences when data is preloaded
    if (action.type === PRELOAD_DATA_ON_LOGIN) {
      const state = store.getState()
      const userSettings = getUserSettings(state)

      isLoadingFromSettings = true
      try {
        if (userSettings?.[SEARCH_TABLE_VISIBILITY_KEY]) {
          store.dispatch(
            setColumnVisibility(userSettings[SEARCH_TABLE_VISIBILITY_KEY]),
          )
        }
        if (userSettings?.[SEARCH_TABLE_ORDER_KEY]) {
          store.dispatch(setColumnOrder(userSettings[SEARCH_TABLE_ORDER_KEY]))
        }
        if (userSettings?.[SEARCH_TABLE_SIZING_KEY]) {
          store.dispatch(setColumnSizing(userSettings[SEARCH_TABLE_SIZING_KEY]))
        }
        // A stored `null` (explicit reset) is intentionally skipped here, so
        // it falls back to the reducer's default (untouched/auto-resolved).
        if (userSettings?.[SEARCH_SOURCE_SCOPE_KEY]) {
          store.dispatch(setSourceScope(userSettings[SEARCH_SOURCE_SCOPE_KEY]))
        }
      } finally {
        isLoadingFromSettings = false
      }
    }

    // Save changes to user preferences when column visibility/order/sizing/source-scope changes
    if (!isLoadingFromSettings) {
      const matched = settingsByAction.find(({ match }) => match(action))
      if (matched) {
        store.dispatch(
          updateUserSettings({
            settings: {
              [matched.key]: matched.getValue(action as never),
            },
          }),
        )
      }
    }

    return result
  }
}

export default searchPreferencesSync
